/*
  Pretty-printers for the cryptic S3 / DynamoDB naming scheme used across MIRA.
  Display layer only: raw strings still flow through handlers untouched, so any
  downstream API call that depends on the original name keeps working.

  Raw shapes:
    experiment:  "20260224_141050:Cubone_overnight-test-1"
    snapshot:    "20260209_000000:Cubone_Snapshots"
    sample:      "1-Sample_1"   |   "3-Sample_3"
    image:       "MIRA_1_A1_20260224_141103_01.jpg"
*/

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Match YYYYMMDD_HHMMSS at the start of a string.
const STAMP_RE = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})(?::|_|$)/;

function parseStamp(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.match(STAMP_RE);
  if (!m) return null;
  const [, yyyy, mo, dd, hh, mi, ss] = m;
  const monthIdx = parseInt(mo, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return {
    date: new Date(Date.UTC(+yyyy, monthIdx, +dd, +hh, +mi, +ss)),
    year: +yyyy,
    month: monthIdx,
    day: +dd,
    hour: +hh,
    minute: +mi,
    second: +ss,
  };
}

function fmt12h(hour, minute) {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/**
 * Friendly experiment / snapshot label.
 *   "20260224_141050:Cubone_overnight-test-1" -> { title: "Cubone overnight-test-1", subtitle: "Feb 24, 2026 · 2:10 PM" }
 *   "20260209_000000:Cubone_Snapshots"        -> { title: "Cubone Snapshots", subtitle: "Feb 9, 2026" }
 * Falls back to the raw string when the format is unexpected.
 */
export function formatExperimentName(raw) {
  if (typeof raw !== 'string' || !raw) return { title: String(raw || ''), subtitle: '' };
  const stamp = parseStamp(raw);
  const colonIdx = raw.indexOf(':');
  const rest = colonIdx >= 0 ? raw.slice(colonIdx + 1) : raw;
  // "Cubone_overnight-test-1" -> "Cubone overnight-test-1"
  const title = rest.replace(/_/g, ' ').trim() || raw;
  if (!stamp) return { title, subtitle: '' };
  const isMidnight = stamp.hour === 0 && stamp.minute === 0 && stamp.second === 0;
  const date = `${MONTHS_SHORT[stamp.month]} ${stamp.day}, ${stamp.year}`;
  const subtitle = isMidnight ? date : `${date} · ${fmt12h(stamp.hour, stamp.minute)}`;
  return { title, subtitle };
}

/**
 * Friendly sample label.
 *   "1-Sample_1" -> "Sample 1"
 *   "3-Sample_3" -> "Sample 3"
 *   "2-MyCustomName" -> "MyCustomName"
 * Numeric prefix becomes a slot hint when the trailing label is generic.
 */
export function formatSampleName(raw) {
  if (typeof raw !== 'string' || !raw) return { title: String(raw || ''), subtitle: '' };
  const m = raw.match(/^(\d+)-(.+)$/);
  if (!m) return { title: raw.replace(/_/g, ' '), subtitle: '' };
  const slot = m[1];
  const name = m[2].replace(/_/g, ' ').trim();
  // "Sample 1" form: collapse to that, with slot hidden (slot == trailing number).
  const generic = name.match(/^Sample\s*(\d+)$/i);
  if (generic && generic[1] === slot) {
    return { title: `Sample ${slot}`, subtitle: '' };
  }
  return { title: name, subtitle: `Slot ${slot}` };
}

/**
 * Friendly image label.
 *   "MIRA_1_A1_20260224_141103_01.jpg" -> { title: "A1", subtitle: "2:11:03 PM" }
 *   Anything we can't parse falls back to the filename.
 */
export function formatImageName(raw) {
  if (typeof raw !== 'string' || !raw) return { title: String(raw || ''), subtitle: '' };
  // MIRA_<sample>_<well>_YYYYMMDD_HHMMSS_<seq>.<ext>
  const m = raw.match(/^MIRA_(\d+)_([A-Za-z]\d+)_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_(\d+)\./);
  if (!m) return { title: raw, subtitle: '' };
  const [, , well, , , , hh, mi, ss] = m;
  const hour = +hh;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return {
    title: well.toUpperCase(),
    subtitle: `${h12}:${mi}:${ss} ${ampm}`,
  };
}

/**
 * Best-effort newest-first sort comparator for raw experiment-like strings.
 * Uses leading timestamp when present; falls back to string compare.
 */
export function compareByStampDesc(a, b) {
  const sa = parseStamp(a);
  const sb = parseStamp(b);
  if (sa && sb) return sb.date.getTime() - sa.date.getTime();
  if (sa) return -1;
  if (sb) return 1;
  return String(b || '').localeCompare(String(a || ''));
}
